import { useEffect, useMemo, useState } from "react";
import { X, Move, ArrowRightLeft, Copy } from "lucide-react";
import { longDay, getMealSlotByDayAndSlot, isSlotEmpty } from "../utils/mealPlanUtils";

/* ════════════════════════════════════════════════════════════════════════
   MEAL MOVE TARGET PICKER MODAL
   Mobile-friendly fallback (and desktop alternative) to drag-and-drop.
   Lets the user pick a target (day, slot) for Move / Duplicate / Swap.
   ════════════════════════════════════════════════════════════════════════ */

const MODE_META = {
  move:      { label: "Move meal",      verb: "Move to",      Icon: Move,            tone: "emerald" },
  duplicate: { label: "Duplicate meal", verb: "Duplicate to", Icon: Copy,            tone: "violet" },
  swap:      { label: "Swap meal",      verb: "Swap with",    Icon: ArrowRightLeft,  tone: "amber" },
};

export function MealMoveTargetPickerModal({
  open,
  mode = "move",          // "move" | "duplicate" | "swap"
  source,                 // { day, slotId, slotLabel }
  weeklyMealPlan = [],
  orderedDays = [],
  slotsCfg = [],
  onPick,                 // ({ day, slotId }) => void
  onClose,
}) {
  const [selected, setSelected] = useState(null); // { day, slotId }

  useEffect(() => {
    if (!open) return undefined;
    setSelected(null);
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const meta = MODE_META[mode] || MODE_META.move;
  const Icon = meta.Icon;

  const filledMap = useMemo(() => {
    const m = {};
    (weeklyMealPlan || []).forEach(d => {
      m[d.day] = m[d.day] || {};
      (slotsCfg || []).forEach(c => {
        m[d.day][c.id] = !isSlotEmpty(getMealSlotByDayAndSlot(weeklyMealPlan, d.day, c.id));
      });
    });
    return m;
  }, [weeklyMealPlan, slotsCfg]);

  if (!open || !source) return null;

  const isSource = (day, slotId) => day === source.day && slotId === source.slotId;

  const confirm = () => {
    if (!selected) return;
    onPick(selected);
  };

  const toneRing = {
    emerald: "ring-emerald-400 bg-transparent dark:bg-transparent border-[color:var(--ara-line-soft)]",
    violet:  "ring-violet-400  bg-transparent  dark:bg-transparent  border-[color:var(--ara-line-soft)]",
    amber:   "ring-amber-400   bg-transparent   dark:bg-transparent   border-[color:var(--ara-line-soft)]",
  }[meta.tone];

  const toneBtn = {
    emerald: "bg-transparent hover:opacity-80",
    violet:  "bg-transparent  hover:opacity-80",
    amber:   "bg-transparent   hover:opacity-80",
  }[meta.tone];

  return (
    <div
      className="fixed inset-0 z-[120] bg-slate-950/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="meal-move-target-picker"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-2xl rounded-3xl bg-white dark:bg-slate-900 shadow-none border border-[color:var(--ara-line-soft)] dark:border-rose-900/40 overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl    flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-extrabold text-gray-900 dark:text-gray-50 leading-tight" data-testid="meal-move-target-title">
              {meta.label}
            </h2>
            <p className="text-xs font-bold text-gray-500 mt-0.5">
              {meta.verb} which day &amp; meal slot?
            </p>
            <p className="text-[11px] font-extrabold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] mt-1">
              From: {longDay(source.day)} · {source.slotLabel}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center justify-center text-gray-400"
            aria-label="Close"
            data-testid="meal-move-target-close"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Picker grid — mini meal table */}
        <div className="px-3 pb-3 overflow-auto flex-1">
          <div className="min-w-fit" style={{ minWidth: `${110 + orderedDays.length * 90}px` }}>
            <div className="grid sticky top-0 bg-transparent dark:bg-transparent border-b border-[color:var(--ara-line-soft)] dark:border-rose-900/30 z-10" style={{ gridTemplateColumns: `110px repeat(${orderedDays.length}, minmax(80px, 1fr))` }}>
              <div className="px-2 py-1.5 text-[10px] font-extrabold uppercase tracking-wider text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">Meal</div>
              {orderedDays.map(d => (
                <div key={d} className="px-1 py-1.5 text-center text-[11px] font-extrabold text-gray-700 dark:text-gray-200 border-l border-[color:var(--ara-line-soft)] dark:border-rose-900/30">
                  {longDay(d).slice(0, 3)}
                </div>
              ))}
            </div>
            {(slotsCfg || []).map(cfg => (
              <div key={cfg.id} className="grid border-b border-rose-50 dark:border-slate-800" style={{ gridTemplateColumns: `110px repeat(${orderedDays.length}, minmax(80px, 1fr))` }}>
                <div className={`px-2 py-2 text-[11px] font-extrabold ${cfg.text}`}>{cfg.label}</div>
                {orderedDays.map(d => {
                  const filled = filledMap[d]?.[cfg.id];
                  const sel = selected && selected.day === d && selected.slotId === cfg.id;
                  const src = isSource(d, cfg.id);
                  const disabled = src; // can't pick the source itself
                  return (
                    <button
                      key={`${d}-${cfg.id}`}
                      disabled={disabled}
                      onClick={() => setSelected({ day: d, slotId: cfg.id })}
                      className={`m-0.5 rounded-lg border text-[10px] font-extrabold py-2 flex flex-col items-center justify-center gap-0.5 transition ${
                        sel
                          ? `ring-2 ${toneRing}`
                          : src
                            ? "bg-gray-100 dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-400 cursor-not-allowed"
                            : filled
                              ? "bg-transparent dark:bg-transparent border-[color:var(--ara-line-soft)] dark:border-rose-800/60 text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] hover:bg-transparent"
                              : "bg-white dark:bg-slate-800 border-dashed border-gray-300 dark:border-slate-700 text-gray-500 hover:border-[color:var(--ara-line-soft)] hover:text-[color:var(--ara-ink-muted)]"
                      }`}
                      data-testid={`meal-move-target-cell-${d}-${cfg.id}`}
                      aria-pressed={sel}
                      title={src ? "Source slot" : filled ? "Has a meal" : "Empty"}
                    >
                      {src ? "Source" : filled ? "Filled" : "Empty"}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-100 dark:border-slate-800 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center px-4 py-2.5 rounded-2xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-sm font-extrabold text-gray-700 dark:text-gray-200"
            data-testid="meal-move-target-cancel"
          >
            Cancel
          </button>
          <button
            disabled={!selected}
            onClick={confirm}
            className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl text-white text-sm font-extrabold shadow-sm active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed ${toneBtn}`}
            data-testid="meal-move-target-confirm"
          >
            <Icon className="w-4 h-4" strokeWidth={1.5} /> {meta.label}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MealMoveTargetPickerModal;
